<script setup lang="ts">
import type { Schemas } from '#shopware'

const props = defineProps<{
    content: Schemas['CmsElementA4gAccordion']
}>()

const componentId = useId()

const items = computed(() => {
    return [...(props.content?.config?.accordion?.value ?? [])].sort((a, b) => a.position - b.position)
})

const faqSchema = computed(() => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.value.map((item) => ({
        '@type': 'Question',
        name: item.title,
        acceptedAnswer: {
            '@type': 'Answer',
            text: item.content,
        },
    })),
}))

useHead(() => (items.value.length > 0
    ? { script: [jsonLdScript(faqSchema.value, `ld-faq-${componentId}`)] }
    : {}))
</script>

<template>
    <div
        v-if="items.length > 0"
        :class="`cms-element-${content.type}`"
        class="collapse-wrapper md:w-full -mx-2 md:m-auto max-w-[768px]"
    >
        <details
            v-for="(item, index) in items"
            :key="index"
            class="collapse group"
            :name="`accordion_${componentId}`"
        >
            <summary
                :id="`trigger_${encodeURIComponent(item.title)}_${index}`"
                class="collapse-title pt-4 pb-3 pl-4 md:pl-3.5 pr-[60px] min-h-0 border-y border-t-secondary border-b-transparent md:border-l-2 md:border-l-transparent list-none [&::-webkit-details-marker]:hidden group-open:bg-neutral-content"
            >
                <span class="text-primary font-semibold">{{ item.title }}</span>
                <div
                    aria-hidden="true"
                    class="btn btn-ghost btn-circle absolute right-0 top-0 m-1.5 group-open:hidden"
                >
                    <MiscIcon name="chevron-down" />
                </div>
                <div
                    aria-hidden="true"
                    class="btn btn-secondary btn-circle absolute right-0 top-0 m-1.5 group-open:!inline-flex"
                >
                    <MiscIcon name="chevron-up" />
                </div>
            </summary>
            <div
                :id="`content_${encodeURIComponent(item.title)}_${index}`"
                class="collapse-content px-0 max-w-[592px]"
                role="region"
                :aria-labelledby="`trigger_${encodeURIComponent(item.title)}_${index}`"
            >
                <div
                    class="p-4 text-sm"
                >
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <div v-html="item.content" />
                </div>
            </div>
        </details>
    </div>
</template>
